// checklist — itens entram um a um; a caixa enche na cor de destaque e o ✓ se desenha a lápis.
import React from 'react';
import { interpolate } from 'remotion';
import type { ChecklistProps } from './schema';
import { CARD, EASE_BACK, EASE_OUT, Pop, SAFE_W, fitFont, onAccent, prog, useGFrame, type GraphicComponentProps } from './common';

const W = SAFE_W - 20;
const PAD_X = 46;
const BOX = 54;
const GAP = 24;

export const Checklist: React.FC<GraphicComponentProps<ChecklistProps>> = ({ props, totalFrames, accent }) => {
  const f = useGFrame();
  const items = props.items.slice(0, 4);
  const n = items.length;
  const first = 3;
  const step = Math.max(6, Math.min(14, Math.floor((totalFrames * 0.6 - first) / Math.max(1, n))));
  const longest = items.reduce((a, b) => (b.length > a.length ? b : a), '');
  const size = fitFont(longest, { weight: 700, max: 44, min: 32, maxWidth: W - PAD_X * 2 - BOX - GAP });
  const ink = onAccent(accent);

  return (
    <div style={{ ...CARD, width: W, padding: `${props.title ? 34 : 38}px ${PAD_X}px 38px` }}>
      {props.title ? (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 28, letterSpacing: 3, textTransform: 'uppercase', color: accent }}>{props.title}</div>
          <div style={{ height: 5, width: 84, background: accent, borderRadius: 3, marginTop: 10 }} />
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {items.map((item, i) => {
          const t0 = first + i * step;
          const inP = prog(f, t0, t0 + 7, EASE_OUT);
          const check = prog(f, t0 + 4, t0 + 9, EASE_BACK);
          const draw = prog(f, t0 + 6, t0 + 13, EASE_OUT);
          const boxScale = interpolate(check, [0, 1], [0.6, 1]);
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: GAP,
                opacity: inP,
                translate: `${((1 - inP) * 28).toFixed(1)}px 0px`,
                filter: `blur(${((1 - inP) * 8).toFixed(2)}px)`,
              }}
            >
              <Pop at={t0 + 5} />
              <div style={{ position: 'relative', width: BOX, height: BOX, flexShrink: 0 }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: 16, border: '4px solid rgba(255,255,255,0.22)', boxSizing: 'border-box' }} />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 16,
                    background: accent,
                    opacity: Math.min(1, check * 1.4),
                    scale: String(Math.max(0.01, boxScale)),
                    boxShadow: `0 6px 18px rgba(0,0,0,0.35)`,
                  }}
                />
                <svg viewBox="0 0 54 54" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
                  <path
                    d="M 14 28 L 23 37 L 41 17"
                    fill="none"
                    stroke={ink}
                    strokeWidth={6.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pathLength={1}
                    strokeDasharray={1}
                    strokeDashoffset={1 - draw}
                  />
                </svg>
              </div>
              <div style={{ fontWeight: 700, fontSize: size, lineHeight: 1.12, color: '#fff' }}>{item}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
